<template>
  <div :class="[$style.config, $style.wrap]">
    <div :class="$style.type">
      <IconMic :class="$style.icon" />
      <span
        :style="data?.fields?.cssOverride as StyleValue"
        :class="$style.label"
      >
        {{ data?.fields?.mediaType?.fields?.label }}
      </span>
    </div>

    <!-- self hosted -->
    <div
      v-if="data?.fields?.podcast?.fields?.file?.url"
      :class="$style.podcastDetail"
    >
      <header :class="$style.header">
        <BaseHeading size="h5" :class="$style.title">
          {{ data?.fields?.title }}
        </BaseHeading>
      </header>
      <time :datetime="data?.fields?.dateTime" :class="$style.date">
        {{ data?.fields?.date }}
      </time>
      <MarkedParser :class="$style.description" :md="data?.fields?.copy" />
      <AspectRatio :class="$style.thumbnail">
        <BasePicture
          :src="`${data?.fields?.poster?.fields?.file?.url}?w=400&h=400&fit=thumb`"
          alt=""
        />
      </AspectRatio>

      <div :class="$style.audioPlayerWrapper">
        <AudioPlayer :src="data?.fields?.podcast?.fields?.file?.url" />
      </div>
    </div>

    <!-- iframed -->
    <template v-else-if="data?.fields?.iframeSrc">
      <iframe
        ref="iframe"
        :height="data?.fields?.iframeHeight"
        :class="$style.podcastIframe"
        :src="data?.fields?.iframeSrc"
        frameborder="0"
        allow="
          accelerometer;
          autoplay;
          encrypted-media;
          gyroscope;
          picture-in-picture;
        "
        enablejsapi="1"
        allowfullscreen
      />
      <div :class="$style.meta">
        <header v-if="data?.fields?.title">
          <BaseHeading as="h3" size="h5" :class="$style.title">
            {{ data?.fields?.title }}
          </BaseHeading>
        </header>
        <time
          v-if="data?.fields?.date"
          :datetime="data?.fields?.dateTime"
          :class="$style.date"
        >
          {{ data?.fields?.date }}
        </time>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { TypePodcastWithoutUnresolvableLinksResponse } from '~/types/contentful'
import type { StyleValue } from 'vue'

defineProps<{
  data: TypePodcastWithoutUnresolvableLinksResponse
}>()
</script>

<style lang="scss" module>
.config {
  --frame-background: rgba(0, 0, 0, 0.75);

  @include breakpoint-variables(
    '--frame-padding',
    (
      base: 0.5em 1.25em,
      sm: 1em 2em,
    )
  );

  --description-color: #afb9c9;

  --title-color: var(--white);

  --playlist-date-color: #667080;
}

.wrap {
  will-change: backdrop-filter;
  backface-visibility: hidden; // remove flicker
  background: var(--frame-background);
  backdrop-filter: blur(4px);
  border-radius: 0.75em;
  padding: var(--frame-padding);
}

.audioPlayerWrapper {
  padding: 1em 0;
}

.meta {
  padding: 1em 0;
}

.title {
  color: var(--title-color);
  text-transform: none;
  margin: 0.5em 0;
}

.description {
  border-top: 1px solid var(--white-alpha25);

  font-size: 14px;
  color: var(--description-color);

  padding: 1em 0 0;
  margin: 1em 0 0;

  display: -webkit-box;
  word-break: break-word;
  text-overflow: ellipsis;
  -webkit-line-clamp: 9;
  line-clamp: 9;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.thumbnail {
  margin: 1em 0;
}

.icon {
  width: 1.25em;
  height: 1.25em;
}

.type {
  display: flex;
  align-items: center;
  padding: 1em 0;
}

.label {
  display: inline-block;

  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;

  margin: 0 0.5em;
  color: var(
    --label-color,
    #fff
  ); // this var is set in contentful, don't change!
}

.date {
  color: var(--playlist-date-color);
  font-weight: bold;
  font-size: 12px;
  display: none;

  @include media('xs') {
    display: block;
  }
}

.podcastIframe {
  width: 100%;
  min-height: 180px;
}
</style>
